import { Link } from "react-router-dom";
import { Tag, ArrowRight } from "lucide-react";
import { Pic } from "@/components/Pic";
import { thumbUrl } from "@/lib/images";
import { imageAlt } from "@/lib/imageAlt";
import { propertyUrl, tr } from "@/lib/translations";
import { useOffers, offersFor, effectiveOffer, offerDetailLabel } from "@/lib/offers";
import { useLang } from "@/context/LanguageContext";

const T = {
  de: { title: "Aktuelle Angebote", all: "Alle {c} Angebote auf {n}", lm: "Last Minute −{p} %" },
  en: { title: "Current offers", all: "All {c} offers in {n}", lm: "Last minute −{p} %" },
  es: { title: "Ofertas actuales", all: "Todas las {c} ofertas en {n}", lm: "Última hora −{p} %" },
  nl: { title: "Actuele aanbiedingen", all: "Alle {c} aanbiedingen op {n}", lm: "Last minute −{p} %" },
  pl: { title: "Aktualne oferty", all: "Wszystkie oferty ({c}) na {n}", lm: "Last minute −{p} %" },
  fr: { title: "Offres actuelles", all: "Toutes les {c} offres à {n}", lm: "Dernière minute −{p} %" },
  it: { title: "Offerte attuali", all: "Tutte le {c} offerte a {n}", lm: "Last minute −{p} %" },
};

/** Rechte Spalte der Inselseite: Objekte mit laufendem Angebot (Last Minute / Marketing-Angebote) – unter dem Steckbrief. */
export const IslandOffers = ({ island, properties, name, show = true, max: maxProp = 4 }) => {
  const { lang, t } = useLang();
  const offers = useOffers();
  const tx = T[lang] || T.en;
  const max = Math.min(12, Math.max(1, Number(maxProp) || 4));
  const deals = properties.map((p) => {
    const lm = p.last_minute_percent > 0 ? tx.lm.replace("{p}", p.last_minute_percent) : null;
    const o = offersFor(offers, p)[0];
    const label = lm || (o ? offerDetailLabel(effectiveOffer(o, p), t, lang) : null);
    return label ? { p, label } : null;
  }).filter(Boolean);
  if (!deals.length || show === false) return null;
  return (
    <section className="rounded-2xl border border-line bg-white p-5" data-testid="island-offers">
      <h2 className="font-serif text-lg mb-3 flex items-center gap-2"><Tag className="w-4 h-4 text-badge-offer" />{tx.title.replace("{n}", name)}</h2>
      <ul className="divide-y divide-line/70">
        {deals.slice(0, max).map(({ p, label }) => {
          const title = tr(p.short_title, lang) || tr(p.title, lang);
          return (
            <li key={p.id} data-testid={`island-offer-${p.slug}`}>
              <Link to={propertyUrl(p)} className="group flex items-center gap-3 py-2.5">
                <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-tint">
                  {p.hero_image && <Pic src={thumbUrl(p.hero_image)} webp={p.webp_ready} alt={imageAlt(p, title, lang)} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-stone-800 group-hover:text-brand">{title}</div>
                  <div className="text-xs text-stone-500 truncate">{p.location}{p.ref_number ? ` · Nr. ${p.ref_number}` : ""}</div>
                  <span className="mt-1 inline-block rounded-full bg-badge-offer px-2 py-px text-[10px] font-semibold uppercase tracking-wider text-white">{label}</span>
                </div>
                <ArrowRight className="w-4 h-4 shrink-0 text-stone-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
              </Link>
            </li>
          );
        })}
      </ul>
      {deals.length > max && <Link to={`/properties?island=${island}&deals=1`} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline" data-testid="island-offers-all">{tx.all.replace("{c}", deals.length).replace("{n}", name)}<ArrowRight className="w-3.5 h-3.5" /></Link>}
    </section>
  );
};
